import { Component } from "react"

/* Components */
import { Input } from "../../components"

/* Modal */
import Modal from "react-modal"

/* Redux */
import { connect } from "react-redux"

/* Translate */
import { t } from "../../translates/translate"

/* REST API */
import { variables, get, create, edit } from "../../api/Operator"

/* Helpers */
import { utils } from "../../helpers"

/* Icons */
import { CloseIcon, CheckIcon, InfoIcon, LinkIcon, TableIcon, OperatorIcon } from "./OperatorIcons"

/* Sweet alert */
import Swal from "../../helpers/alert"


/* EMPTY FORM STATE */
const EMPTY = {
    id: 0,
    type: "create",
    status: "loading",
    studios: [],
    tables: [],
    name: "",
    slug: "",
    startpoint: "",
    balanceURL: "",
    debitURL: "",
    creditURL: "",
    webhookURL: "",
    roundCloseURL: "",
    licenseNumber: "",
    tableIDs: [],
    waiting: false,
}

/* SLUG OF AN OPERATOR — LOWERCASE LETTERS, NUMBERS, A DASH AND AN UNDERSCORE */
const SLUG_PATTERN = /^[a-z0-9_-]+$/

/* LENGTH OF THE LICENSE COLUMN — Operators.licenseNumber IS A STRING(50) */
const LICENSE_LENGTH = 50


/* Widget Operator Form */
class OperatorForm extends Component {

    constructor() {
        super()
        this.state = { visible: false, ...EMPTY }
    }

    /* Open Action */
    open = ({ type = "create", id = 0 }) => {

        this.setState({ visible: true, ...EMPTY, type, id: parseInt(id) ? parseInt(id) : 0 }, () => {
            this.load()
        })
    }

    /* Close Action */
    close = () => {
        this.setState({ visible: false, ...EMPTY })
    }

    /* Load Action — create reads the variables, edit reads the operator together with them */
    load = () => {

        /* Fields */
        const { type, id } = this.state

        /* Set Status */
        this.setState({ status: "loading" })

        /* Edit */
        if (type === "edit") {

            get({ id }).then((response) => {
                if (response.status === 200) {
                    this.setState({
                        status: "ok",
                        studios: response.data.studios,
                        tables: response.data.tables,
                        name: utils.checkValue(response.data.operator.name, ""),
                        slug: utils.checkValue(response.data.operator.slug, ""),
                        startpoint: utils.checkValue(response.data.operator.startpoint, ""),
                        balanceURL: utils.checkValue(response.data.operator.balanceURL, ""),
                        debitURL: utils.checkValue(response.data.operator.debitURL, ""),
                        creditURL: utils.checkValue(response.data.operator.creditURL, ""),
                        webhookURL: utils.checkValue(response.data.operator.webhookURL, ""),
                        roundCloseURL: utils.checkValue(response.data.operator.roundCloseURL, ""),
                        licenseNumber: utils.checkValue(response.data.operator.licenseNumber, ""),
                        tableIDs: response.data.tableIDs,
                    })
                }
                else {
                    this.setState({ status: "error" })
                    Swal.fire(utils.notification(this.message(response), "error"))
                    this.close()
                }
            }).catch(() => {
                this.setState({ status: "error" })
                Swal.fire(utils.notification(t("swal.generic"), "error"))
                this.close()
            })

            return
        }

        /* Create */
        variables().then((response) => {
            if (response.status === 200) {
                this.setState({ status: "ok", studios: response.data.studios, tables: response.data.tables })
            }
            else {
                this.setState({ status: "error" })
                Swal.fire(utils.notification(this.message(response), "error"))
                this.close()
            }
        }).catch(() => {
            this.setState({ status: "error" })
            Swal.fire(utils.notification(t("swal.generic"), "error"))
            this.close()
        })

    }

    /* Read The Error Message Of A Response */
    message = (response) => {

        if (response && response.data && response.data.message) {
            return response.data.message
        }

        if (response && response.status === 498) {
            return t("swal.invalidKey")
        }

        if (response && (response.status === 401 || response.status === 403)) {
            return t("swal.noAccess")
        }

        return t("swal.generic")
    }

    /* Table Groups — the access section keeps the tables of one studio together */
    getGroups = () => {

        /* Fields */
        const { studios, tables } = this.state

        /* Collect Studios */
        const groups = studios.map((item) => ({
            id: parseInt(item.id),
            name: item.name,
            slug: item.slug,
            tables: tables.filter((table) => parseInt(table.studioID) === parseInt(item.id)),
        }))

        /* Collect The Rest — a table of a removed studio still needs a place */
        const rest = tables.filter((table) => !studios.some((item) => parseInt(item.id) === parseInt(table.studioID)))

        /* Check The Rest */
        if (rest.length > 0) {
            groups.push({ id: 0, name: t("operator.withoutStudio"), slug: "", tables: rest })
        }

        /* Send Result — an empty studio has nothing to open */
        return groups.filter((item) => item.tables.length > 0)
    }

    /* Toggle Table */
    toggleTable = (id) => {

        /* Fields */
        const { tableIDs } = this.state
        const tableID = parseInt(id)

        /* Check Selection */
        if (tableIDs.includes(tableID)) {
            this.setState({ tableIDs: tableIDs.filter((item) => parseInt(item) !== tableID) })
        }
        else {
            this.setState({ tableIDs: [...tableIDs, tableID] })
        }
    }

    /* Toggle Every Table Of One Studio */
    toggleGroup = (group) => {

        /* Fields */
        const { tableIDs } = this.state
        const ids = group.tables.map((item) => parseInt(item.id))
        const selected = ids.filter((item) => tableIDs.includes(item))

        /* Check Selection */
        if (selected.length === ids.length) {
            this.setState({ tableIDs: tableIDs.filter((item) => !ids.includes(parseInt(item))) })
            return
        }

        this.setState({ tableIDs: [...new Set([...tableIDs, ...ids])] })
    }

    /* Save Action */
    save = () => {

        /* Fields */
        const { type, id, name, slug, startpoint, balanceURL, debitURL, creditURL, webhookURL, roundCloseURL, licenseNumber, tableIDs } = this.state
        const { reload } = this.props

        /* Check Name */
        if (!name.trim()) {
            this._name.error(t("operator.error.emptyName"))
            return
        }

        /* Check Slug */
        if (!slug.trim()) {
            this._slug.error(t("operator.error.emptySlug"))
            return
        }

        /* Check Slug Format */
        if (!SLUG_PATTERN.test(slug.trim().toLowerCase())) {
            this._slug.error(t("operator.error.slugFormat"))
            return
        }

        /* Check Startpoint */
        if (!startpoint.trim()) {
            this._startpoint.error(t("operator.error.emptyStartpoint"))
            return
        }

        /* Check Startpoint Format */
        if (!/^https?:\/\/[^\s]+$/i.test(startpoint.trim())) {
            this._startpoint.error(t("operator.error.startpointFormat"))
            return
        }

        /* Check Balance Path */
        if (!balanceURL.trim()) {
            this._balance.error(t("operator.error.emptyBalance"))
            return
        }

        /* Check Debit Path */
        if (!debitURL.trim()) {
            this._debit.error(t("operator.error.emptyDebit"))
            return
        }

        /* Check Credit Path */
        if (!creditURL.trim()) {
            this._credit.error(t("operator.error.emptyCredit"))
            return
        }

        /* Check Paths Format */
        const paths = [
            { value: balanceURL, ref: this._balance },
            { value: debitURL, ref: this._debit },
            { value: creditURL, ref: this._credit },
            { value: webhookURL, ref: this._webhook },
            { value: roundCloseURL, ref: this._roundClose },
        ]

        /* Check Every Path */
        for (const path of paths) {
            if (path.value.trim() !== "" && !path.value.trim().startsWith("/")) {
                path.ref.error(t("operator.error.pathFormat"))
                return
            }
        }

        /* Start Waiting */
        this.setState({ waiting: true })

        /* Prepare Request */
        const fields = {
            name: name.trim(),
            slug: slug.trim().toLowerCase(),
            startpoint: startpoint.trim(),
            balanceURL: balanceURL.trim(),
            debitURL: debitURL.trim(),
            creditURL: creditURL.trim(),
            webhookURL: webhookURL.trim(),
            roundCloseURL: roundCloseURL.trim(),
            licenseNumber: licenseNumber.trim(),
            tableIDs,
        }

        /* Send to REST API */
        const request = type === "edit" ? edit({ id, ...fields }) : create(fields)

        request.then((response) => {
            if (response.status === 200) {
                Swal.fire(utils.notification(type === "edit" ? t("operator.savedSuccess") : t("operator.addedSuccess"), "success"))
                reload()
                this.close()
            }
            else {
                Swal.fire(utils.notification(this.message(response), "error"))
            }
        }).catch(() => {
            Swal.fire(utils.notification(t("swal.generic"), "error"))
        }).finally(() => {
            this.setState({ waiting: false })
        })

    }

    /* Draw Main Section — the name and the slug of an operator */
    _main = () => {

        /* Fields */
        const { name, slug } = this.state

        return (
            <div className="operator--section">

                <div className="operator--section-head">
                    <div className="operator--section-title">
                        <OperatorIcon size={13} />
                        {t("operator.mainSection")}
                    </div>
                </div>

                <div className="operator--fields">
                    <Input ref={(ref) => this._name = ref} label={t("operator.name")} value={name} onChange={(event) => this.setState({ name: event.target.value })} />
                    <Input ref={(ref) => this._slug = ref} label="Slug" value={slug} onChange={(event) => this.setState({ slug: event.target.value.toLowerCase() })} />
                </div>

            </div>
        )
    }

    /* Draw Integration Section — the startpoint and every path glued to it */
    _integration = () => {

        /* Fields */
        const { startpoint, balanceURL, debitURL, creditURL, webhookURL, roundCloseURL, licenseNumber } = this.state

        return (
            <div className="operator--section">

                <div className="operator--section-head">
                    <div className="operator--section-title">
                        <LinkIcon />
                        {t("operator.integrationSection")}
                    </div>
                </div>

                <Input ref={(ref) => this._startpoint = ref} label="Startpoint" value={startpoint} onChange={(event) => this.setState({ startpoint: event.target.value })} />

                <div className="operator--hint">
                    <InfoIcon />
                    <span>{t("operator.pathHint")}</span>
                </div>

                <div className="operator--fields">
                    <Input ref={(ref) => this._balance = ref} label="Balance URL" value={balanceURL} onChange={(event) => this.setState({ balanceURL: event.target.value })} />
                    <Input ref={(ref) => this._debit = ref} label="Debit URL" value={debitURL} onChange={(event) => this.setState({ debitURL: event.target.value })} />
                </div>

                <div className="operator--fields">
                    <Input ref={(ref) => this._credit = ref} label="Credit URL" value={creditURL} onChange={(event) => this.setState({ creditURL: event.target.value })} />
                    <Input ref={(ref) => this._webhook = ref} label="Webhook URL" value={webhookURL} onChange={(event) => this.setState({ webhookURL: event.target.value })} />
                </div>

                <div className="operator--fields">
                    <Input ref={(ref) => this._roundClose = ref} label="Round close URL" value={roundCloseURL} onChange={(event) => this.setState({ roundCloseURL: event.target.value })} />
                    <Input ref={(ref) => this._license = ref} label={t("operator.licenseNumber")} value={licenseNumber} maxLength={LICENSE_LENGTH} onChange={(event) => this.setState({ licenseNumber: event.target.value })} />
                </div>

            </div>
        )
    }

    /* Draw Table Access — every studio keeps its own group */
    _tables = () => {

        /* Fields */
        const { tableIDs, tables } = this.state
        const groups = this.getGroups()

        return (
            <div className="operator--section">

                <div className="operator--section-head">
                    <div className="operator--section-title">
                        <TableIcon />
                        {t("operator.tableAccess")}
                        <span className={`operator--counter ${tableIDs.length === 0 ? "empty" : ""}`}>{tableIDs.length} / {tables.length}</span>
                    </div>
                </div>

                {groups.length === 0
                    ? <div className="operator--chips-empty">{t("operator.noTables")}</div>
                    : groups.map((group, index) =>
                        <div className="operator--group" key={`${index}`}>

                            <div className="operator--group-head">

                                <div className="operator--group-title">
                                    {group.name}
                                    <span className="operator--group-count">{group.tables.filter((item) => tableIDs.includes(parseInt(item.id))).length} / {group.tables.length}</span>
                                </div>

                                <div onClick={() => this.toggleGroup(group)} className="operator--link">
                                    {group.tables.every((item) => tableIDs.includes(parseInt(item.id))) ? t("common.clearAll") : t("common.selectAll")}
                                </div>

                            </div>

                            <div className="operator--chips">
                                {group.tables.map((item, ind) =>
                                    <div
                                        onClick={() => this.toggleTable(item.id)}
                                        className={`operator--chip ${tableIDs.includes(parseInt(item.id)) ? "active" : ""}`}
                                        key={`${ind}`}
                                    >
                                        <div className="operator--chip-mark"><CheckIcon size={11} /></div>
                                        <div className="operator--chip-text">
                                            <div className="operator--chip-name">{item.name}</div>
                                            {item.slug ? <div className="operator--chip-slug">{item.slug}</div> : null}
                                        </div>
                                    </div>
                                )}
                            </div>

                        </div>
                    )
                }

                <div className="operator--hint">
                    <InfoIcon />
                    <span>{t("operator.tableAccessHint")}</span>
                </div>

            </div>
        )
    }

    /* Draw Body */
    _body = () => {

        /* Fields */
        const { status } = this.state

        /* Check Status */
        if (status !== "ok") {
            return <div className="operator--modal-loading"><div className="operator--loading" /></div>
        }

        return (
            <div className="operator--modal-body">

                {/* Draw Main Section */}
                {this._main()}

                {/* Draw Integration Section */}
                {this._integration()}

                {/* Draw Table Access */}
                {this._tables()}

            </div>
        )
    }

    /* Draw Footer */
    _footer = () => {

        /* Fields */
        const { status, waiting } = this.state

        /* Check Status */
        if (status !== "ok") {
            return null
        }

        return (
            <div className="operator--modal-footer">

                <div onClick={() => this.close()} className="operator--button ghost">{t("common.cancel")}</div>

                {waiting
                    ? <div className="operator--button primary"><div className="operator--loading small" /></div>
                    : <div onClick={() => this.save()} className="operator--button primary">{t("common.save")}</div>
                }

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { visible, type } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={() => this.close()}
                className="operator--modal"
                overlayClassName="operator--overlay"
                closeTimeoutMS={200}
            >

                <div className="operator--modal-header">

                    <div>
                        <h2>{type === "edit" ? t("operator.editTitle") : t("operator.addTitle")}</h2>
                        <p>{type === "edit" ? t("operator.editSubtitle") : t("operator.createSubtitle")}</p>
                    </div>

                    <div onClick={() => this.close()} className="operator--modal-close">
                        <CloseIcon />
                    </div>

                </div>

                {/* Draw Body */}
                {this._body()}

                {/* Draw Footer */}
                {this._footer()}

            </Modal>
        )
    }

}

const mapStateToProps = (state) => ({
    language: state.language,
})

export default connect(mapStateToProps, null, null, { forwardRef: true })(OperatorForm)
